import { Injectable } from '@angular/core';
import {
  GoogleAuthProvider, signInWithRedirect, getRedirectResult, signOut,
  onAuthStateChanged, User
} from 'firebase/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { FirebaseService } from './firebase.service';

const ADMIN_EMAIL = 'johnl242@gmail.com';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject = new BehaviorSubject<User | null>(null);
  readonly user$: Observable<User | null> = this.userSubject.asObservable();

  // v1.0.165 — was signInWithPopup, which throws auth/popup-blocked on any
  // browser (or embedded/sandboxed preview) that blocks window.open popups —
  // confirmed live, this made Google sign-in completely unusable in some
  // environments regardless of the account being valid. signInWithRedirect
  // navigates the whole page instead of opening a popup, so it can't be
  // popup-blocked; the tradeoff is the result now arrives asynchronously
  // after the redirect back, via getRedirectResult() below, rather than as
  // the return value of the original call.
  private redirectResultPromise: Promise<void>;

  constructor(private fb: FirebaseService) {
    onAuthStateChanged(this.fb.auth, user => {
      this.userSubject.next(user);
    });
    this.redirectResultPromise = this.consumeRedirectResult();
  }

  private async consumeRedirectResult(): Promise<void> {
    const result = await getRedirectResult(this.fb.auth);
    if (!result) { return; }
    const user = result.user;
    // Check if reseller exists (non-admins must be registered).
    // v1.0.165 — was looking up `resellers/${user.uid}`, but reseller
    // records are created by the admin BEFORE the reseller ever signs in
    // (via ResellersComponent.save(), which uses Firebase's pushNew() —
    // a random push-key, never the reseller's own Google UID, which the
    // admin has no way to know in advance). That lookup could never match
    // any real record — every reseller login was silently rejected
    // regardless of whether they were actually registered. Match on the
    // Gmail address instead, which IS what the admin actually enters when
    // creating the reseller record.
    if (user.email !== ADMIN_EMAIL) {
      const all = await this.fb.read<Record<string, any>>('resellers');
      const emailLower = (user.email ?? '').toLowerCase();
      const match = Object.values(all ?? {}).find(r => (r?.email ?? '').toLowerCase() === emailLower);
      if (!match || !match.active) {
        await signOut(this.fb.auth);
        throw new Error('Your account is not registered or has been deactivated. Contact admin.');
      }
    }
  }

  get currentUser(): User | null { return this.userSubject.value; }
  get isLoggedIn(): boolean      { return !!this.userSubject.value; }
  get isAdmin(): boolean         { return this.userSubject.value?.email === ADMIN_EMAIL; }

  /**
   * Resolves the signed-in reseller's actual /resellers/<pushKey> record id
   * — NOT the Firebase Auth uid. Every reseller-owned record (customers,
   * licenses) is keyed by this push-key elsewhere (admin's Issue License
   * dropdown, resellerName() lookup, license filters all match against it),
   * so a reseller-created record keyed by their raw Firebase uid would be
   * invisible/unmatched everywhere else in the admin UI. Looked up by email
   * each call rather than cached at login time so it stays correct even if
   * the admin edits/re-creates the reseller record later.
   */
  async resolveResellerId(): Promise<string> {
    const user = this.currentUser;
    if (!user || this.isAdmin) { return ''; }
    const all = await this.fb.read<Record<string, any>>('resellers');
    const emailLower = (user.email ?? '').toLowerCase();
    const match = Object.entries(all ?? {}).find(([, r]) => (r?.email ?? '').toLowerCase() === emailLower);
    return match ? match[0] : '';
  }

  /**
   * True only if the signed-in reseller currently has an active /resellers
   * record — re-checked live (not cached), so a reseller deactivated after
   * they already signed in loses access on their very next navigation
   * rather than staying signed in until their Firebase session expires.
   */
  async isActiveReseller(): Promise<boolean> {
    const user = this.currentUser;
    if (!user || this.isAdmin) { return false; }
    const all = await this.fb.read<Record<string, any>>('resellers');
    const emailLower = (user.email ?? '').toLowerCase();
    const match = Object.values(all ?? {}).find(r => (r?.email ?? '').toLowerCase() === emailLower);
    return !!match && !!match.active;
  }

  signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    return signInWithRedirect(this.fb.auth, provider);
  }

  // Called once on app boot (e.g. from the login page / app component) to
  // pick up the result of a redirect that just completed. Resolves once,
  // throws the same validation errors signInWithGoogle used to throw
  // synchronously when popups were used.
  consumePendingRedirect(): Promise<void> {
    return this.redirectResultPromise;
  }

  async logout(): Promise<void> {
    return signOut(this.fb.auth);
  }

  getIdToken(): Promise<string> {
    return this.userSubject.value?.getIdToken() ?? Promise.resolve('');
  }
}
